import PropTypes from "prop-types";
import {
  createContext,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
} from "react";
// utils
import axios, { axiosV2, WAREHOUSE_AUTH_V2 } from "../utils/axios";
import { gatherBrowserInfo, getAuthBrowserInfo } from "../utils/gatherBrowserInfo";
import { PATH_DASHBOARD } from "../routes/paths";
import localStorageAvailable from "../utils/localStorageAvailable";
import { useNavigate } from "react-router-dom";
//
import {
  isValidToken,
  isImpersonationToken,
  setSession,
  setRefresh,
  setLevel,
  setWarehouseSelection,
  getWarehouseSelection,
  getSelectionTokenValue,
} from "./utils";
import { setDateTimeFormats } from "../utils/formatTime";
import { setNumberFormatContext } from "../utils/formatNumber";
const backendUrl = process.env.REACT_APP_BACKEND_URL;
const backendUrlV2 = process.env.REACT_APP_BACKEND_URL_V2;

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  paymentMethods: null,
  requiresWarehouseSelection: false,
  selectionToken: null,
  warehouses: [],
};

// V2 returns warehouseId/franchiseId as ids with the details in `warehouse`/`franchise`.
// The app reads details from user.warehouseId (with franchiseId populated), so map V2 onto that shape.
const toWarehouseUser = (user) => {
  if (!user?.warehouse) return user;
  return {
    ...user,
    warehouseId: {
      ...user.warehouse,
      franchiseId: user.franchise || user.warehouse.franchiseId,
    },
  };
};

const reducer = (state, action) => {
  if (action.type === "INITIAL") {
    return {
      isInitialized: true,
      isAuthenticated: action?.payload?.isAuthenticated,
      user: action?.payload?.user,
      paymentMethods: action?.payload?.paymentMethods?.results,
      requiresWarehouseSelection: false,
      selectionToken: action?.payload?.selectionToken || null,
      warehouses: action?.payload?.warehouses || [],
    };
  }
  if (action.type === "LOGIN") {
    return {
      ...state,
      isAuthenticated: true,
      requiresWarehouseSelection: false,
      user: action?.payload?.user,
      paymentMethods: action?.payload?.paymentMethods?.results,
    };
  }
  if (action.type === "REGISTER") {
    return {
      ...state,
      isAuthenticated: true,
      user: action?.payload?.user,
      paymentMethods: action?.payload?.paymentMethods?.results,
    };
  }

  if (action.type === "RESET") {
    return {
      ...state,
      isAuthenticated: true,
      user: action?.payload?.user,
    };
  }

  if (action.type === "LOGOUT") {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      paymentMethods: null,
      requiresWarehouseSelection: false,
      selectionToken: null,
      warehouses: [],
    };
  }
  if (action.type === "WAREHOUSE_SELECTION") {
    return {
      ...state,
      requiresWarehouseSelection: !!action.payload.requiresWarehouseSelection,
      selectionToken: action.payload.selectionToken || null,
      warehouses: action.payload.warehouses || [],
    };
  }
  if (action.type === "UPDATE_USER") {
    return {
      ...state,
      user: action?.payload?.user,
    };
  }
  if (action.type === "UPDATE_PAYMENT_METHOD") {
    return {
      ...state,
      paymentMethods: action?.payload?.paymentMethods,
    };
  }
  if (action.type === "UPDATE_WAREHOUSE") {
    return {
      ...state,
      user: {
        ...state.user,
        warehouseId: action.payload.warehouseId,
      },
    };
  }
  if (action.type === "DELETE_PAYMENT_METHOD") {
    return {
      ...state,
      paymentMethods: action?.payload?.paymentMethods,
    };
  }

  return state;
};

export const AuthContext = createContext(null);

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const storageAvailable = localStorageAvailable();

  useEffect(() => {
    const franchise = state.user?.warehouseId?.franchiseId || state.user?.franchiseId;
    setDateTimeFormats(franchise);
    setNumberFormatContext(franchise);
  }, [state.user?.franchiseId, state.user?.warehouseId?.franchiseId]);

  const initialize = useCallback(async () => {
    try {
      if (window.location.pathname === "/impersonate") {
        dispatch({
          type: "INITIAL",
          payload: { isAuthenticated: false, user: null, paymentMethods: null },
        });
        return;
      }

      const refreshToken = storageAvailable
        ? localStorage.getItem("refreshToken")
        : "";
      let accessToken = storageAvailable
        ? localStorage.getItem("accessToken")
        : "";

      if (refreshToken) {
        let activeRefreshToken = refreshToken;
        // Impersonation sessions come from the V1 exchange and are not accepted by the V2 staff endpoints.
        const impersonating = isImpersonationToken(refreshToken);
        const authUrl = impersonating
          ? `${backendUrl}/authw`
          : `${backendUrlV2}${WAREHOUSE_AUTH_V2}`;
        if (!isValidToken(accessToken)) {
          const refreshResponse = await axios.post(`${authUrl}/refresh-tokens`, {
            refreshToken,
            browserInfo: impersonating
              ? { userAgent: window.navigator.userAgent }
              : { browser: getAuthBrowserInfo(await gatherBrowserInfo()).browser },
          });
          const refreshedTokens = refreshResponse.data?.tokens || refreshResponse.data;
          accessToken = refreshedTokens?.access?.token;
          activeRefreshToken = refreshedTokens?.refresh?.token;
          if (!accessToken || !activeRefreshToken) throw new Error("Unable to refresh session");
        }

        setSession(accessToken);
        setRefresh(activeRefreshToken);

        const response = await axios.post(`${authUrl}/get-profile`, {
          refreshToken: activeRefreshToken,
        });

        const { paymentMethods } = response.data;
        const user = toWarehouseUser(response.data.user);

        if (user?.warehouseId?.levels?.length) {
          if (!localStorage.getItem("levelSelected")) {
            setLevel(user?.warehouseId?.levels[0]);
          }
        }

        user.displayName = `${user?.firstName} ${user?.lastName} `;
        if (user?.warehouseId?.status === "inActive") {
          dispatch({
            type: "INITIAL",
            payload: {
              isAuthenticated: false,
              user: null,
              paymentMethods: null,
            },
          });
        } else {
          dispatch({
            type: "INITIAL",
            payload: {
              isAuthenticated: true,
              user,
              paymentMethods,
              ...(!impersonating && getWarehouseSelection()),
            },
          });
        }
      } else {
        dispatch({
          type: "INITIAL",
          payload: {
            isAuthenticated: false,
            user: null,
            paymentMethods: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      setSession(null);
      setRefresh(null);
      dispatch({
        type: "INITIAL",
        payload: {
          isAuthenticated: false,
          user: null,
          paymentMethods: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const navigateToLanding = useCallback((user) => {
    if (user.permissions?.canViewDashboard) {
      navigate("/dashboard/app");
    } else if (user.permissions?.canViewStaff) {
      navigate(PATH_DASHBOARD.hrm.staff);
    } else if (user.permissions?.canViewStock) {
      navigate("/dashboard/stock/display");
    } else if (user?.permissions?.canViewLiveStock) {
      navigate("/dashboard/stock/live");
    } else if (user?.permissions?.canViewAdjustment) {
      navigate("/dashboard/stock/adjustment");
    } else if (user?.permissions?.canViewItem) {
      navigate("/dashboard/item/list");
    } else if (user?.permissions?.canViewPurchseOrder) {
      navigate("/dashboard/stock/purchaseOrders");
    } else if (user?.permissions?.canViewSuppliers) {
      navigate("/dashboard/supplier/list");
    } else if (user?.permissions?.canViewPurchseOrderInvoices) {
      navigate("/dashboard/invoice/list");
    } else {
      navigate("/dashboard/settings/new")
    }
  }, [navigate]);

  // Shared by login and select-warehouse: both return { tokens, user } for a warehouse session.
  const startWarehouseSession = useCallback(({ tokens, user: sessionUser, paymentMethods }) => {
    if (!tokens?.access?.token || !tokens?.refresh?.token || !sessionUser) {
      throw new Error("Invalid authentication response");
    }

    const user = toWarehouseUser(sessionUser);

    user.displayName = `${user?.firstName} ${user?.lastName}`;

    setSession(tokens.access.token);
    setRefresh(tokens.refresh.token);
    dispatch({
      type: "LOGIN",
      payload: {
        user,
        paymentMethods,
      },
    });
    navigateToLanding(user);
  }, [navigateToLanding]);

  // LOGIN
  const login = useCallback(async (userName, password, ipAddress, browserInfo) => {
    const response = await axiosV2.post(`${WAREHOUSE_AUTH_V2}/login`, {
      userName,
      password,
      ipAddress,
      browserInfo: getAuthBrowserInfo(browserInfo),
    });
    const { requiresWarehouseSelection, selectionToken, warehouses } = response.data;

    if (requiresWarehouseSelection) {
      setWarehouseSelection(selectionToken, warehouses);
      dispatch({
        type: "WAREHOUSE_SELECTION",
        payload: { requiresWarehouseSelection, selectionToken, warehouses },
      });
      return response.data;
    }

    setWarehouseSelection(null);
    dispatch({ type: "WAREHOUSE_SELECTION", payload: {} });
    startWarehouseSession(response.data);
    return response.data;
  }, [startWarehouseSession]);

  // SELECT WAREHOUSE (completes a pending login, or switches the current warehouse)
  const selectWarehouse = useCallback(async (warehouseId) => {
    const selectionToken = getSelectionTokenValue(state.selectionToken);
    if (!selectionToken) {
      throw new Error("Warehouse selection has expired. Please log in again.");
    }

    const response = await axiosV2.post(`${WAREHOUSE_AUTH_V2}/select-warehouse`, {
      selectionToken,
      warehouseId,
      browserInfo: getAuthBrowserInfo(await gatherBrowserInfo()),
    });

    // The selection token is single-use; keep the warehouse list for Switch Warehouse.
    startWarehouseSession(response.data);
    setWarehouseSelection(null, state.warehouses);
    dispatch({
      type: "WAREHOUSE_SELECTION",
      payload: { warehouses: state.warehouses },
    });
    return response.data;
  }, [state.selectionToken, state.warehouses, startWarehouseSession]);

  // SWITCH WAREHOUSE (authenticated; the backend revokes the previous session)
  const switchWarehouse = useCallback(async (warehouseId) => {
    const browserInfo = await gatherBrowserInfo();
    const response = await axiosV2.post(`${WAREHOUSE_AUTH_V2}/switch-warehouse`, {
      refreshToken: localStorage.getItem("refreshToken"),
      warehouseId,
      browserInfo: {
        ...getAuthBrowserInfo(browserInfo),
        device: /Mobi|Android|iPhone|iPad/i.test(window.navigator.userAgent) ? "mobile" : "desktop",
      },
    });
    startWarehouseSession(response.data);
    return response.data;
  }, [startWarehouseSession]);

  const cancelWarehouseSelection = useCallback(() => {
    setWarehouseSelection(null);
    dispatch({ type: "WAREHOUSE_SELECTION", payload: {} });
  }, []);

  const exchangeImpersonation = useCallback(async (code, browserInfo) => {
    const response = await axios.post(`${backendUrl}/authw/impersonation/exchange`, {
      code,
      browserInfo,
    });
    const { tokens, user, scope, paymentMethods } = response.data;
    if (
      scope !== "warehouse"
      || user?.auth !== "warehouse"
      || !user?.isImpersonating
      || !tokens?.access?.token
      || !tokens?.refresh?.token
    ) {
      throw new Error("Invalid warehouse impersonation response");
    }

    setSession(tokens.access.token);
    setRefresh(tokens.refresh.token);
    setWarehouseSelection(null);
    user.displayName = user.displayName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    dispatch({ type: "LOGIN", payload: { user, paymentMethods } });
    navigate("/dashboard/app", { replace: true });
    return user;
  }, [navigate]);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const response = await axios.post(
      "https://api-dev-minimal-v4.vercel.app/api/account/register",
      {
        email,
        password,
        firstName,
        lastName,
      }
    );
    const { accessToken, user } = response.data;

    localStorage.setItem("accessToken", accessToken);

    dispatch({
      type: "REGISTER",
      payload: {
        user,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    setRefresh(null);
    setLevel(null);
    setWarehouseSelection(null);
    localStorage.removeItem("levelPopOver");
    dispatch({
      type: "LOGOUT",
    });
  }, []);

  // Reset
  const resetPassword = useCallback(
    async (old_password, new_password, user) => {
      const response = await axios.patch(
        `${backendUrl}/warehouseUser/changePassword/${user?.id}`,
        {
          old_password,
          new_password,
        }
      );

      dispatch({
        type: "RESET",
        payload: {
          user,
        },
      });
      return response;
    },
    []
  );

  const updateUser = useCallback((updatedUser) => {
    updatedUser.displayName = `${updatedUser?.firstName} ${updatedUser?.lastName}`;
    dispatch({
      type: "UPDATE_USER",
      payload: {
        user: updatedUser,
      },
    });
  }, []);

  const addPaymentMethod = useCallback(
    (newPaymentMethod) => {
      const updatedPaymentMethods = [...state.paymentMethods, newPaymentMethod];
      dispatch({
        type: "UPDATE_PAYMENT_METHOD",
        payload: {
          paymentMethods: updatedPaymentMethods,
        },
      });
    },
    [state.paymentMethods]
  );

  const updatePaymentMethod = useCallback(
    (updatedPaymentMethod) => {
      dispatch({
        type: "UPDATE_PAYMENT_METHOD",
        payload: {
          paymentMethods: state.paymentMethods.map((method) =>
            method.id === updatedPaymentMethod.id
              ? updatedPaymentMethod
              : method
          ),
        },
      });
    },
    [state.paymentMethods]
  );
  const deletePaymentMethod = useCallback(
    (paymentMethodId) => {
      const updatedPaymentMethods = state.paymentMethods.filter(
        (method) => method.id !== paymentMethodId
      );
      dispatch({
        type: "UPDATE_PAYMENT_METHOD",
        payload: {
          paymentMethods: updatedPaymentMethods,
        },
      });
    },
    [state.paymentMethods]
  );

  const updateWarehouse = useCallback((updateWarehouse) => {
    dispatch({
      type: "UPDATE_WAREHOUSE",
      payload: {
        warehouseId: updateWarehouse,
      },
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      availablePaymentOptions: state.paymentMethods,
      requiresWarehouseSelection: state.requiresWarehouseSelection,
      selectionToken: state.selectionToken,
      warehouses: state.warehouses,
      method: "jwt",
      login,
      selectWarehouse,
      switchWarehouse,
      cancelWarehouseSelection,
      exchangeImpersonation,
      register,
      logout,
      resetPassword,
      updateUser,
      addPaymentMethod,
      updatePaymentMethod,
      deletePaymentMethod,
      updateWarehouse,
    }),
    [
      state.isAuthenticated,
      state.isInitialized,
      state.user,
      state.paymentMethods,
      state.requiresWarehouseSelection,
      state.selectionToken,
      state.warehouses,
      login,
      selectWarehouse,
      switchWarehouse,
      cancelWarehouseSelection,
      exchangeImpersonation,
      logout,
      register,
      resetPassword,
      updateUser,
      addPaymentMethod,
      updatePaymentMethod,
      deletePaymentMethod,
      updateWarehouse,
    ]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
